import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useProgress } from '../context/ProgressContext';

// Define the theme types
export type ColorModeType = 'light' | 'dark' | 'system';
export type BackgroundType = 'gradient' | 'solid';
export type TransitionType = 'bounce' | 'smooth' | 'sharp' | 'none' | 'drastic';

// Define the theme template interface
export interface ThemeTemplate {
  id: string;
  name: string;
  colorMode: ColorModeType;
  fontSize: number;
  lineSpacing: number;
  multiplier: number;
  backgroundType: BackgroundType;
  backgroundColor: string;
  activeTextColor: string;
  inactiveTextColor: string;
  isDefault: boolean;
  currentLineScale?: number; // How much to scale the current line (default: 1.1)
  currentFontScale?: number; // How much to scale the font of current line (default: 1.2)
  currentLineFontFactor?: number; // Controls font size of current line relative to other lines (0-1)
  currentLineFontWeight?: number; // Controls font weight of current line (0-1 scale, 0=same as others, 1=bold)
  letterSpacing?: string; // Controls letter spacing (e.g. "-0.04em")
  transitionType?: TransitionType; // Transition effect for current line
  frostedEffect?: boolean; // Whether to apply frosted effect to current line
  frostedOpacity?: number; // Opacity level for frosted effect (0-1)
  frostedColorDark?: string; // Color for frosted effect in dark mode (default: rgba(25, 25, 35, opacity))
  frostedColorLight?: string; // Color for frosted effect in light mode (default: rgba(250, 250, 255, opacity))
  // For backward compatibility
  glassEffect?: boolean; 
  glassOpacity?: number; 
}

// Default templates
const defaultTemplates: ThemeTemplate[] = [
  {
    id: 'default_template_1',
    name: 'Default Dark',
    colorMode: 'dark',
    fontSize: 1,
    lineSpacing: 1,
    multiplier: 0.3,
    backgroundType: 'gradient',
    backgroundColor: '#121212',
    activeTextColor: '#ffffff',
    inactiveTextColor: 'rgba(255, 255, 255, 0.6)',
    isDefault: true,
    currentLineScale: 1.1,
    currentFontScale: 1.2,
    currentLineFontFactor: 0.7,
    currentLineFontWeight: 0.6,
    letterSpacing: '-0.04em',
    transitionType: 'bounce',
    frostedEffect: false,
    frostedOpacity: 0.3,
    frostedColorDark: 'rgba(25, 25, 35, 0.3)',
    frostedColorLight: 'rgba(250, 250, 255, 0.3)',
    glassEffect: false,
    glassOpacity: 0.3
  }
];

// Define the interface for user templates in Supabase
export interface UserTemplateRecord {
  id?: string;
  user_id?: string;
  name: string;
  description?: string;
  priority: number;
  template_data: ThemeTemplate;
  created_at?: string;
  updated_at?: string;
}

// Define layout settings type
export interface LayoutSettings {
  fontSize: number;
  lineSpacing: number;
  letterSpacing: string;
  currentLineScale: number;
  currentLineFontFactor: number;
  currentLineFontWeight: number;
  frostedEffect: boolean;
  frostedOpacity: number;
  multiplier: number;
}

// Context type
interface LyricsThemeContextType {
  currentTheme: ColorModeType;
  setCurrentTheme: (theme: ColorModeType) => void;
  colorMode: ColorModeType;
  setColorMode: (mode: ColorModeType) => void;
  isDarkMode: boolean;
  isLightMode: boolean;
  isTemplateDark: boolean;
  dominantColor: string | null;
  setDominantColor: (color: string | null) => void;
  isDominantColorLoading: boolean;
  setIsDominantColorLoading: (loading: boolean) => void;
  backgroundGradient: string | null;
  setBackgroundGradient: (gradient: string | null) => void;
  fontSize: number;
  setFontSize: (size: number) => void;
  lineSpacing: number;
  setLineSpacing: (spacing: number) => void;
  multiplier: number;
  setMultiplier: (multiplier: number) => void;
  backgroundType: BackgroundType;
  setBackgroundType: (type: BackgroundType) => void;
  backgroundColor: string;
  setBackgroundColor: (color: string) => void;
  activeTextColor: string;
  setActiveTextColor: (color: string) => void;
  inactiveTextColor: string;
  setInactiveTextColor: (color: string) => void;
  currentLineScale: number;
  setCurrentLineScale: (scale: number) => void;
  currentLineFontFactor: number;
  setCurrentLineFontFactor: (factor: number) => void;
  transitionType: TransitionType;
  setTransitionType: (type: TransitionType) => void;
  frostedEffect: boolean;
  setFrostedEffect: (enabled: boolean) => void;
  frostedOpacity: number;
  setFrostedOpacity: (opacity: number) => void;
  frostedColorDark: string;
  setFrostedColorDark: (color: string) => void;
  frostedColorLight: string;
  setFrostedColorLight: (color: string) => void;
  templates: ThemeTemplate[];
  dbTemplates: UserTemplateRecord[];
  isLoadingDbTemplates: boolean;
  dbTemplateError: string | null;
  fetchUserTemplates: () => Promise<void>;
  currentTemplate: ThemeTemplate | null;
  saveTemplate: (templateData: Omit<ThemeTemplate, 'id' | 'isDefault'>, existingId?: string) => Promise<string>;
  deleteTemplate: (id: string) => Promise<void>;
  applyTemplate: (id: string) => void;
  setDefaultTemplate: (id: string) => Promise<void>;
  currentLineFontWeight: number;
  setCurrentLineFontWeight: (weight: number) => void;
  letterSpacing: string;
  setLetterSpacing: (spacing: string) => void;
}

// Create context with default values
const LyricsThemeContext = createContext<LyricsThemeContextType>({
  currentTheme: 'dark',
  setCurrentTheme: () => {},
  colorMode: 'dark',
  setColorMode: () => {},
  isDarkMode: true,
  isLightMode: false,
  isTemplateDark: true,
  dominantColor: null,
  setDominantColor: () => {},
  isDominantColorLoading: false,
  setIsDominantColorLoading: () => {},
  backgroundGradient: null,
  setBackgroundGradient: () => {},
  fontSize: 1,
  setFontSize: () => {},
  lineSpacing: 1,
  setLineSpacing: () => {},
  multiplier: 0.3,
  setMultiplier: () => {},
  backgroundType: 'gradient',
  setBackgroundType: () => {},
  backgroundColor: '#121212',
  setBackgroundColor: () => {},
  activeTextColor: '#ffffff',
  setActiveTextColor: () => {},
  inactiveTextColor: 'rgba(255, 255, 255, 0.6)',
  setInactiveTextColor: () => {},
  currentLineScale: 1.1,
  setCurrentLineScale: () => {},
  currentLineFontFactor: 0.7,
  setCurrentLineFontFactor: () => {},
  transitionType: 'bounce',
  setTransitionType: () => {},
  frostedEffect: false,
  setFrostedEffect: () => {},
  frostedOpacity: 0.3,
  setFrostedOpacity: () => {},
  frostedColorDark: 'rgba(25, 25, 35, 0.3)',
  setFrostedColorDark: () => {},
  frostedColorLight: 'rgba(250, 250, 255, 0.3)',
  setFrostedColorLight: () => {},
  templates: defaultTemplates,
  dbTemplates: [],
  isLoadingDbTemplates: false,
  dbTemplateError: null,
  fetchUserTemplates: () => Promise.resolve(),
  currentTemplate: null,
  saveTemplate: () => Promise.resolve(''),
  deleteTemplate: () => Promise.resolve(),
  applyTemplate: () => {},
  setDefaultTemplate: () => Promise.resolve(),
  currentLineFontWeight: 0.6,
  setCurrentLineFontWeight: () => {},
  letterSpacing: '-0.04em',
  setLetterSpacing: () => {}
});

// Provider Component
export const LyricsThemeProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  // Import currentTrackId and trackIdJustChanged from ProgressContext
  const { currentTrackId, trackIdJustChanged } = useProgress();

  // Load settings from localStorage with special handling for currentLineFontWeight
  const loadFromStorage = (key: string, defaultValue: any) => {
    try {
      const storedValue = localStorage.getItem(key);
      
      // No value in localStorage, return default
      if (!storedValue) {
        return defaultValue;
      }
      
      // Handle special cases
      if (key === 'transition_type') {
        // Clean up the transition type value by removing quotes if necessary
        try {
          const parsedValue = JSON.parse(storedValue);
          if (typeof parsedValue === 'string' && 
             ['bounce', 'smooth', 'sharp', 'none', 'drastic'].includes(parsedValue)) {
            return parsedValue;
          }
        } catch (parseError) {
          // If it's not valid JSON, try using the raw string if it's a valid enum value
          if (['bounce', 'smooth', 'sharp', 'none', 'drastic'].includes(storedValue)) {
            // Save the corrected value for next time
            localStorage.setItem(key, JSON.stringify(storedValue));
            return storedValue;
          }
        }
        // If we get here, the value is invalid so return default
        localStorage.setItem(key, JSON.stringify(defaultValue));
        return defaultValue;
      }
      
      // Special handling for currentLineFontWeight to ensure 0 is handled correctly
      if (key === 'current_line_font_weight') {
        try {
          const parsedValue = JSON.parse(storedValue);
          if (typeof parsedValue === 'number' && !isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= 1) {
            console.log(`Loaded ${key} from localStorage:`, parsedValue);
            return parsedValue;
          } else {
            console.warn(`Invalid ${key} in localStorage:`, parsedValue, 'using default:', defaultValue);
            // Save the default value to localStorage to fix the issue
            localStorage.setItem(key, JSON.stringify(defaultValue));
            return defaultValue;
          }
        } catch (parseError) {
          console.error(`Error parsing ${key} from localStorage:`, parseError);
          // Try to convert to number directly as a fallback
          const numValue = parseFloat(storedValue);
          if (!isNaN(numValue) && numValue >= 0 && numValue <= 1) {
            console.log(`Converted ${key} from string to number:`, numValue);
            // Save the properly formatted value
            localStorage.setItem(key, JSON.stringify(numValue));
            return numValue;
          }
          // Save the default value to localStorage to fix the issue
          localStorage.setItem(key, JSON.stringify(defaultValue));
          return defaultValue;
        }
      }
      
      // For all other keys, use regular parsing with better error handling
      try {
        return JSON.parse(storedValue);
      } catch (parseError) {
        console.error(`Error parsing ${key} from localStorage:`, parseError);
        // Recover by saving the default value
        localStorage.setItem(key, JSON.stringify(defaultValue));
        return defaultValue;
      }
    } catch (error) {
      console.error(`Error loading ${key} from localStorage:`, error);
      return defaultValue;
    }
  };

  // Theme state
  const [currentTheme, setCurrentTheme] = useState<ColorModeType>(
    loadFromStorage('lyrics_theme', 'dark')
  );
  const [colorMode, setColorMode] = useState<ColorModeType>(
    loadFromStorage('color_mode', 'dark')
  );
  
  // Calculate isDarkMode and isLightMode
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isLightMode, setIsLightMode] = useState(false);
  
  // State for album art derived values
  const [dominantColor, setDominantColor] = useState<string | null>(null);
  const [isDominantColorLoading, setIsDominantColorLoading] = useState(false);
  const [backgroundGradient, setBackgroundGradient] = useState<string | null>(null);
  
  // State for theme values
  const [fontSize, setFontSize] = useState(loadFromStorage('font_size', 1));
  const [lineSpacing, setLineSpacing] = useState(loadFromStorage('line_spacing', 1));
  const [multiplier, setMultiplier] = useState(loadFromStorage('multiplier', 0.3));
  const [letterSpacing, setLetterSpacing] = useState(loadFromStorage('letter_spacing', '-0.04em'));
  const [backgroundType, setBackgroundType] = useState<BackgroundType>(
    loadFromStorage('background_type', 'gradient')
  );
  const [backgroundColor, setBackgroundColor] = useState(
    loadFromStorage('background_color', '#121212')
  );
  const [activeTextColor, setActiveTextColor] = useState(
    loadFromStorage('active_text_color', '#ffffff')
  );
  const [inactiveTextColor, setInactiveTextColor] = useState(
    loadFromStorage('inactive_text_color', 'rgba(255, 255, 255, 0.6)')
  );
  const [currentLineScale, setCurrentLineScale] = useState(
    loadFromStorage('current_line_scale', 1.1)
  );
  const [currentLineFontFactor, setCurrentLineFontFactor] = useState(
    loadFromStorage('current_line_font_factor', 0.7)
  );
  const [transitionType, setTransitionType] = useState<TransitionType>(
    loadFromStorage('transition_type', 'bounce')
  );
  const [frostedEffect, setFrostedEffect] = useState(
    loadFromStorage('frosted_effect', false)
  );
  const [frostedOpacity, setFrostedOpacity] = useState(
    loadFromStorage('frosted_opacity', 0.3)
  );
  const [frostedColorDark, setFrostedColorDark] = useState(
    loadFromStorage('frosted_color_dark', 'rgba(25, 25, 35, 0.3)')
  );
  const [frostedColorLight, setFrostedColorLight] = useState(
    loadFromStorage('frosted_color_light', 'rgba(250, 250, 255, 0.3)')
  );

  // Add state for current line font weight
  const [currentLineFontWeight, setCurrentLineFontWeight] = useState(
    loadFromStorage('current_line_font_weight', 0.6)
  );

  // Templates state
  const [templates, setTemplates] = useState<ThemeTemplate[]>(() => {
    // Try to load templates from localStorage
    const savedTemplates = loadFromStorage('templates', null);
    
    // If templates exist in localStorage, use those
    if (savedTemplates && Array.isArray(savedTemplates) && savedTemplates.length > 0) {
      return savedTemplates;
    }
    
    // Otherwise use the default templates
    return defaultTemplates;
  });
  
  const [currentTemplate, setCurrentTemplate] = useState<ThemeTemplate | null>(null);

  // State for user templates from Supabase
  const [dbTemplates, setDbTemplates] = useState<UserTemplateRecord[]>([]);
  const [isLoadingDbTemplates, setIsLoadingDbTemplates] = useState(false);
  const [dbTemplateError, setDbTemplateError] = useState<string | null>(null);

  // Fetch user templates from Supabase
  const fetchUserTemplates = async () => {
    try {
      setIsLoadingDbTemplates(true);
      setDbTemplateError(null);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log('No user found, skipping template fetch');
        setIsLoadingDbTemplates(false);
        return;
      }
      
      // Fetch templates for current user
      const { data, error } = await supabase
        .from('user_templates')
        .select('*')
        .order('priority', { ascending: false });
      
      if (error) {
        console.error('Error fetching user templates:', error);
        setDbTemplateError(error.message);
        setIsLoadingDbTemplates(false);
        return;
      }
      
      console.log('Fetched user templates:', data);
      setDbTemplates(data || []);
      
      // Find the default template - it's the one with highest priority (100)
      const defaultTemplate = (data || []).find(template => template.priority === 100);
      
      if (defaultTemplate && defaultTemplate.template_data) {
        console.log('Found default template by priority:', defaultTemplate.template_data.name);
        
        // Update template_data to mark it as default
        if (!defaultTemplate.template_data.isDefault) {
          // Update the template in Supabase to mark it as default
          await supabase
            .from('user_templates')
            .update({
              template_data: {
                ...defaultTemplate.template_data,
                isDefault: true
              }
            })
            .eq('id', defaultTemplate.id);
        }
        
        // Apply the default template
        console.log('Auto-applying default template:', defaultTemplate.template_data.name);
        applyTemplateData(defaultTemplate.template_data);
        
        // Set as the current template
        setCurrentTemplate(defaultTemplate.template_data);
        
        // Track usage in analytics
        if (defaultTemplate.id) {
          trackTemplateUsage(defaultTemplate.id);
        }
      }
    } catch (error) {
      console.error('Error in fetchUserTemplates:', error);
      setDbTemplateError('Failed to fetch templates');
    } finally {
      setIsLoadingDbTemplates(false);
    }
  };

  // Helper function to apply a template's data (extracted for reuse)
  const applyTemplateData = (template: ThemeTemplate) => {
    // Apply all the settings from the template
    if (template.colorMode) {
      setColorMode(template.colorMode);
      setCurrentTheme(template.colorMode);
    }
    
    if (template.fontSize !== undefined) {
      setFontSize(template.fontSize);
    }
    
    if (template.lineSpacing !== undefined) {
      setLineSpacing(template.lineSpacing);
    }
    
    if (template.multiplier !== undefined) {
      setMultiplier(template.multiplier);
    }
    
    if (template.backgroundType) {
      setBackgroundType(template.backgroundType);
    }
    
    if (template.backgroundColor) {
      setBackgroundColor(template.backgroundColor);
    }
    
    if (template.activeTextColor) {
      setActiveTextColor(template.activeTextColor);
    }
    
    if (template.inactiveTextColor) {
      setInactiveTextColor(template.inactiveTextColor);
    }
    
    if (template.currentLineScale !== undefined) {
      setCurrentLineScale(template.currentLineScale);
    }
    
    if (template.currentLineFontFactor !== undefined) {
      setCurrentLineFontFactor(template.currentLineFontFactor);
    }
    
    if (template.currentLineFontWeight !== undefined) {
      setCurrentLineFontWeight(template.currentLineFontWeight);
      
      // Apply the CSS variable directly for immediate effect
      // For a value of 0, explicitly set "0" rather than an empty string
      document.documentElement.style.setProperty(
        '--current-line-font-weight', 
        template.currentLineFontWeight === 0 ? "0" : template.currentLineFontWeight.toString()
      );
      const fontWeightValue = 400 + (300 * template.currentLineFontWeight);
      document.documentElement.style.setProperty('--lyrics-line-font-weight', fontWeightValue.toString());
      console.log('Applied currentLineFontWeight from template:', template.currentLineFontWeight, 
                 'CSS value:', document.documentElement.style.getPropertyValue('--current-line-font-weight'));
    }
    
    if (template.letterSpacing) {
      setLetterSpacing(template.letterSpacing);
    }
    
    if (template.transitionType) {
      setTransitionType(template.transitionType);
    }
    
    if (template.frostedEffect !== undefined) {
      setFrostedEffect(template.frostedEffect);
    }
    
    if (template.frostedOpacity !== undefined) {
      setFrostedOpacity(template.frostedOpacity);
    }
    
    if (template.frostedColorDark) {
      setFrostedColorDark(template.frostedColorDark);
    }
    
    if (template.frostedColorLight) {
      setFrostedColorLight(template.frostedColorLight);
    }
    
    // Save settings to localStorage
    try {
      // Save the individual settings to localStorage with proper JSON.stringify
      localStorage.setItem('lyrics_theme', JSON.stringify(template.colorMode));
      localStorage.setItem('color_mode', JSON.stringify(template.colorMode));
      localStorage.setItem('font_size', JSON.stringify(template.fontSize));
      localStorage.setItem('line_spacing', JSON.stringify(template.lineSpacing));
      localStorage.setItem('multiplier', JSON.stringify(template.multiplier));
      localStorage.setItem('background_type', JSON.stringify(template.backgroundType));
      localStorage.setItem('background_color', JSON.stringify(template.backgroundColor));
      localStorage.setItem('active_text_color', JSON.stringify(template.activeTextColor));
      localStorage.setItem('inactive_text_color', JSON.stringify(template.inactiveTextColor));
    
      // Save the new properties to localStorage
      if (template.currentLineScale !== undefined) {
        localStorage.setItem('current_line_scale', JSON.stringify(template.currentLineScale));
      }
      if (template.currentLineFontFactor !== undefined) {
        localStorage.setItem('current_line_font_factor', JSON.stringify(template.currentLineFontFactor));
      }
      if (template.currentLineFontWeight !== undefined) {
        localStorage.setItem('current_line_font_weight', JSON.stringify(template.currentLineFontWeight));
      }
      if (template.letterSpacing !== undefined) {
        localStorage.setItem('letter_spacing', JSON.stringify(template.letterSpacing));
      }
      if (template.transitionType !== undefined) {
        // Make sure transition type is properly stringified
        localStorage.setItem('transition_type', JSON.stringify(template.transitionType));
      }
      if (template.frostedEffect !== undefined) {
        localStorage.setItem('frosted_effect', JSON.stringify(template.frostedEffect));
      }
      if (template.frostedOpacity !== undefined) {
        localStorage.setItem('frosted_opacity', JSON.stringify(template.frostedOpacity));
      }
      if (template.frostedColorDark !== undefined) {
        localStorage.setItem('frosted_color_dark', JSON.stringify(template.frostedColorDark));
      }
      if (template.frostedColorLight !== undefined) {
        localStorage.setItem('frosted_color_light', JSON.stringify(template.frostedColorLight));
      }
      
      console.log("Template settings saved to localStorage successfully");
    } catch (error) {
      console.error("Error saving template settings to localStorage:", error);
    }
  };
  
  // Load user templates on mount and when user changes
  useEffect(() => {
    fetchUserTemplates();
    
    // Subscribe to auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        fetchUserTemplates();
      }
    });
    
    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);
  
  // Apply a template by ID
  const applyTemplate = (id: string) => {
    // First try to find the template in the local array
    let template = templates.find(t => t.id === id);
    
    // If not found in local templates, check in DB templates
    if (!template) {
      const dbTemplate = dbTemplates.find(t => t.id === id || t.template_data.id === id);
      if (dbTemplate) {
        template = dbTemplate.template_data;
        
        // Track usage in Supabase
        if (dbTemplate.id) {
          trackTemplateUsage(dbTemplate.id);
        }
      }
    } else {
      // For local templates, try to find a DB equivalent to track usage
      const dbTemplate = dbTemplates.find(t => t.template_data.id === id);
      if (dbTemplate && dbTemplate.id) {
        trackTemplateUsage(dbTemplate.id);
      }
    }
    
    if (template) {
      // Update current template
      setCurrentTemplate(template);
      
      // Apply all the settings from the template
      applyTemplateData(template);
      
      // Write current settings to localStorage
      saveLayoutToLocalStorage();
    }
  };

  // One-time initialization to ensure all CSS variables are properly set
  useEffect(() => {
    // Set all CSS variables on mount to ensure consistency
    const cssVars = {
      '--lyrics-line-font-size': `calc(28px * ${fontSize})`,
      '--lyrics-line-line-height': `calc(1.5 * ${lineSpacing})`,
      '--lyrics-line-margin-bottom': `calc(34px * ${lineSpacing})`,
      '--lyrics-line-letter-spacing': letterSpacing,
      '--current-line-scale': currentLineScale.toString(),
      '--current-line-font-factor': currentLineFontFactor.toString(),
      '--current-line-font-weight': currentLineFontWeight.toString(),
      '--frosted-effect-opacity': frostedOpacity.toString(),
      '--lyrics-line-font-weight': (400 + (300 * currentLineFontWeight)).toString()
    };
    
    // Apply all CSS variables
    Object.entries(cssVars).forEach(([variable, value]) => {
      document.documentElement.style.setProperty(variable, value);
    });
    
    console.log('Initialized CSS variables:', cssVars);
  }, []); // Empty dependency array for one-time initialization

  // Update localStorage when settings change
  useEffect(() => {
    localStorage.setItem('lyrics_theme', JSON.stringify(currentTheme));
  }, [currentTheme]);
  
  useEffect(() => {
    localStorage.setItem('color_mode', JSON.stringify(colorMode));
  }, [colorMode]);
  
  useEffect(() => {
    localStorage.setItem('dominant_color', JSON.stringify(dominantColor));
    
    // Set the dominant color as a CSS variable
    if (dominantColor) {
      document.documentElement.style.setProperty('--dominant-color', dominantColor);
    } else {
      document.documentElement.style.removeProperty('--dominant-color');
    }
  }, [dominantColor]);
  
  useEffect(() => {
    localStorage.setItem('background_gradient', JSON.stringify(backgroundGradient));
    
    // Set the background gradient as a CSS variable
    if (backgroundGradient) {
      document.documentElement.style.setProperty('--background-gradient', backgroundGradient);
    } else {
      document.documentElement.style.removeProperty('--background-gradient');
    }
  }, [backgroundGradient]);
  
  useEffect(() => {
    localStorage.setItem('font_size', JSON.stringify(fontSize));
  }, [fontSize]);
  
  useEffect(() => {
    localStorage.setItem('line_spacing', JSON.stringify(lineSpacing));
  }, [lineSpacing]);

  useEffect(() => {
    localStorage.setItem('multiplier', JSON.stringify(multiplier));
  }, [multiplier]);

  useEffect(() => {
    localStorage.setItem('background_type', JSON.stringify(backgroundType));
  }, [backgroundType]);
  
  useEffect(() => {
    localStorage.setItem('background_color', JSON.stringify(backgroundColor));
    document.documentElement.style.setProperty('--color-background', backgroundColor);

    // Convert hex to rgb for css variables
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '0, 0, 0';
    };

    // Set RGB version for variable use
    document.documentElement.style.setProperty('--background-color-rgb', hexToRgb(backgroundColor));
  }, [backgroundColor]);
  
  // Update text color settings
  useEffect(() => {
    localStorage.setItem('active_text_color', JSON.stringify(activeTextColor));
    document.documentElement.style.setProperty('--lyrics-line-color-current', activeTextColor);
  }, [activeTextColor]);
  
  useEffect(() => {
    localStorage.setItem('inactive_text_color', JSON.stringify(inactiveTextColor));
    document.documentElement.style.setProperty('--lyrics-line-color', inactiveTextColor);
  }, [inactiveTextColor]);

  useEffect(() => {
    localStorage.setItem('theme_templates', JSON.stringify(templates));
  }, [templates]);

  // Calculate if dark mode or light mode based on color mode setting
  useEffect(() => {
    if (colorMode === 'system') {
      // Check system preference
      const systemPrefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(systemPrefersDark);
      setIsLightMode(!systemPrefersDark);
      
      // Add listener for system preference changes
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        setIsDarkMode(e.matches);
        setIsLightMode(!e.matches);
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Set based on explicit setting
      setIsDarkMode(colorMode === 'dark');
      setIsLightMode(colorMode === 'light');
    }
  }, [colorMode]);

  // Find default template on init and set initial template if none selected
  useEffect(() => {
    // First check if there's a saved current template in localStorage
    const savedTemplateId = localStorage.getItem('current_template');
    if (savedTemplateId) {
      // Try to find the template in local templates first
      let template = templates.find(t => t.id === savedTemplateId);
      
      // If not found in local templates, check in DB templates
      if (!template && dbTemplates.length > 0) {
        const dbTemplate = dbTemplates.find(t => 
          t.id === savedTemplateId || 
          t.template_data.id === savedTemplateId
        );
        if (dbTemplate) {
          template = dbTemplate.template_data;
        }
      }
      
      // If we found the template, apply it
      if (template) {
        console.log('Applying saved template:', template.name);
        setCurrentTemplate(template);
        
        // Apply all the template settings
        setCurrentTheme(template.colorMode);
        setColorMode(template.colorMode);
        setFontSize(template.fontSize);
        setLineSpacing(template.lineSpacing);
        setMultiplier(template.multiplier);
        setBackgroundType(template.backgroundType);
        setBackgroundColor(template.backgroundColor);
        setActiveTextColor(template.activeTextColor || '#ffffff');
        setInactiveTextColor(template.inactiveTextColor || 'rgba(255, 255, 255, 0.6)');
        
        if (template.currentLineScale !== undefined) {
          setCurrentLineScale(template.currentLineScale);
        }
        if (template.currentLineFontFactor !== undefined) {
          setCurrentLineFontFactor(template.currentLineFontFactor);
        }
        if (template.currentLineFontWeight !== undefined) {
          setCurrentLineFontWeight(template.currentLineFontWeight);
          // Apply CSS variables immediately
          document.documentElement.style.setProperty('--current-line-font-weight', template.currentLineFontWeight.toString());
          const fontWeightValue = 400 + (300 * template.currentLineFontWeight);
          document.documentElement.style.setProperty('--lyrics-line-font-weight', fontWeightValue.toString());
        }
        if (template.letterSpacing !== undefined) {
          setLetterSpacing(template.letterSpacing);
        }
        if (template.transitionType !== undefined) {
          setTransitionType(template.transitionType);
        }
        if (template.frostedEffect !== undefined) {
          setFrostedEffect(template.frostedEffect);
        }
        if (template.frostedOpacity !== undefined) {
          setFrostedOpacity(template.frostedOpacity);
        }
        if (template.frostedColorDark !== undefined) {
          setFrostedColorDark(template.frostedColorDark);
        }
        if (template.frostedColorLight !== undefined) {
          setFrostedColorLight(template.frostedColorLight);
        }
        
        return; // Skip the default template application
      }
    }
    
    // If no saved template was found or applied, use default template
    const defaultTemplate = templates.find(t => t.isDefault);
    if (defaultTemplate) {
      setCurrentTemplate(defaultTemplate);
      
      // Apply the default template settings if no settings have been explicitly saved
      if (!localStorage.getItem('lyrics_theme')) {
        setCurrentTheme(defaultTemplate.colorMode);
        setColorMode(defaultTemplate.colorMode);
        setFontSize(defaultTemplate.fontSize);
        setLineSpacing(defaultTemplate.lineSpacing);
        setMultiplier(defaultTemplate.multiplier);
        setBackgroundType(defaultTemplate.backgroundType);
        setBackgroundColor(defaultTemplate.backgroundColor);
        setActiveTextColor(defaultTemplate.activeTextColor || '#ffffff');
        setInactiveTextColor(defaultTemplate.inactiveTextColor || 'rgba(255, 255, 255, 0.6)');
        
        // Make sure font weight is explicitly set
        if (defaultTemplate.currentLineFontWeight !== undefined) {
          console.log('Setting initial font weight from default template:', defaultTemplate.currentLineFontWeight);
          setCurrentLineFontWeight(defaultTemplate.currentLineFontWeight);
          
          // Apply CSS variables immediately
          document.documentElement.style.setProperty('--current-line-font-weight', defaultTemplate.currentLineFontWeight.toString());
          const fontWeightValue = 400 + (300 * defaultTemplate.currentLineFontWeight);
          document.documentElement.style.setProperty('--lyrics-line-font-weight', fontWeightValue.toString());
        }
      }
    }
    
    // Apply currentLineFontWeight from localStorage (if exists) to CSS variables immediately
    const storedFontWeight = localStorage.getItem('current_line_font_weight');
    if (storedFontWeight) {
      try {
        const fontWeightValue = JSON.parse(storedFontWeight);
        if (typeof fontWeightValue === 'number' && !isNaN(fontWeightValue)) {
          console.log('Found font weight in localStorage, applying:', fontWeightValue);
          document.documentElement.style.setProperty('--current-line-font-weight', fontWeightValue.toString());
          const calculatedWeight = 400 + (300 * fontWeightValue);
          document.documentElement.style.setProperty('--lyrics-line-font-weight', calculatedWeight.toString());
        }
      } catch (error) {
        console.error('Error parsing font weight from localStorage:', error);
      }
    }
  }, [templates, dbTemplates]);

  // Update colors when color mode changes
  useEffect(() => {
    // Update CSS variables based on color mode
    if (colorMode === 'dark') {
      document.documentElement.style.setProperty('--lyrics-line-color', 'rgba(255, 255, 255, 0.5)');
      document.documentElement.style.setProperty('--lyrics-line-color-current', '#ffffff');
      document.documentElement.style.setProperty('--lyrics-line-hover-color', 'rgba(255, 255, 255, 0.7)');
    } else {
      document.documentElement.style.setProperty('--lyrics-line-color', 'rgba(0, 0, 0, 0.5)');
      document.documentElement.style.setProperty('--lyrics-line-color-light', 'rgba(0, 0, 0, 0.7)');
      document.documentElement.style.setProperty('--lyrics-line-color-current', dominantColor || '#000000');
      document.documentElement.style.setProperty('--lyrics-line-hover-color-light', 'rgba(0, 0, 0, 0.7)');
    }
  }, [colorMode, dominantColor]);

  // Batch style updates in a single useEffect
  useEffect(() => {
    // Save to localStorage
    localStorage.setItem('line_spacing', JSON.stringify(lineSpacing));
    localStorage.setItem('multiplier', JSON.stringify(multiplier));
    localStorage.setItem('background_type', JSON.stringify(backgroundType));
    localStorage.setItem('background_color', JSON.stringify(backgroundColor));
    localStorage.setItem('active_text_color', JSON.stringify(activeTextColor));
    localStorage.setItem('inactive_text_color', JSON.stringify(inactiveTextColor));
    localStorage.setItem('theme_templates', JSON.stringify(templates));

    // Update DOM styles
    document.documentElement.style.setProperty('--color-background', backgroundColor);
    
    // Convert hex to rgb for css variables
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '0, 0, 0';
    };

    // Set RGB version for variable use
    document.documentElement.style.setProperty('--background-color-rgb', hexToRgb(backgroundColor));
    document.documentElement.style.setProperty('--lyrics-line-color-current', activeTextColor);
    document.documentElement.style.setProperty('--lyrics-line-color', inactiveTextColor);
  }, [lineSpacing, multiplier, backgroundType, backgroundColor, activeTextColor, inactiveTextColor, templates]);

  // Save frosted effect settings to localStorage
  useEffect(() => {
    localStorage.setItem('frosted_effect', JSON.stringify(frostedEffect));
    
    // Set frosted effect as CSS variable
    document.documentElement.style.setProperty('--frosted-effect-enabled', frostedEffect ? '1' : '0');
  }, [frostedEffect]);
  
  useEffect(() => {
    localStorage.setItem('frosted_opacity', JSON.stringify(frostedOpacity));
    
    // Set frosted opacity as CSS variable
    document.documentElement.style.setProperty('--frosted-effect-opacity', frostedOpacity.toString());
  }, [frostedOpacity]);

  useEffect(() => {
    localStorage.setItem('frosted_color_dark', JSON.stringify(frostedColorDark));
    
    // Set frosted color dark as CSS variable
    document.documentElement.style.setProperty('--frost-color-dark', frostedColorDark);
  }, [frostedColorDark]);

  useEffect(() => {
    localStorage.setItem('frosted_color_light', JSON.stringify(frostedColorLight));
    
    // Set frosted color light as CSS variable
    document.documentElement.style.setProperty('--frost-color-light', frostedColorLight);
  }, [frostedColorLight]);

  useEffect(() => {
    localStorage.setItem('current_line_scale', JSON.stringify(currentLineScale));
    
    // Set the current line scale as a CSS variable
    document.documentElement.style.setProperty('--current-line-scale', currentLineScale.toString());
  }, [currentLineScale]);
  
  useEffect(() => {
    localStorage.setItem('current_line_font_factor', JSON.stringify(currentLineFontFactor));
    
    // Set the current line font factor as a CSS variable
    document.documentElement.style.setProperty('--current-line-font-factor', currentLineFontFactor.toString());
  }, [currentLineFontFactor]);

  // Add effect for current line font weight
  useEffect(() => {
    try {
      // Save to localStorage with proper error handling
      localStorage.setItem('current_line_font_weight', JSON.stringify(currentLineFontWeight));
      
      // Set the current line font weight as a CSS variable
      // For a value of 0, explicitly set "0" rather than an empty string
      document.documentElement.style.setProperty(
        '--current-line-font-weight', 
        currentLineFontWeight === 0 ? "0" : currentLineFontWeight.toString()
      );
      
      // Also set a directly usable font-weight value for CSS fallback
      const fontWeightValue = 400 + (300 * currentLineFontWeight);
      document.documentElement.style.setProperty('--lyrics-line-font-weight', fontWeightValue.toString());
      
      // For debugging
      console.log('Updated currentLineFontWeight:', currentLineFontWeight, 
                  'CSS variable:', document.documentElement.style.getPropertyValue('--current-line-font-weight'),
                  'Font weight value:', fontWeightValue);
    } catch (error) {
      console.error('Error saving currentLineFontWeight:', error);
    }
  }, [currentLineFontWeight]);

  // Add effect for letter spacing
  useEffect(() => {
    localStorage.setItem('letter_spacing', JSON.stringify(letterSpacing));
    
    // Set the letter spacing as a CSS variable
    document.documentElement.style.setProperty('--lyrics-line-letter-spacing', letterSpacing);
  }, [letterSpacing]);

  // Add effect for transition type
  useEffect(() => {
    try {
      // Save to localStorage with proper JSON stringification
      localStorage.setItem('transition_type', JSON.stringify(transitionType));
      console.log('Saved transition_type to localStorage:', transitionType);
    } catch (error) {
      console.error('Error saving transition_type to localStorage:', error);
    }
  }, [transitionType]);

  // Function to track template usage in Supabase
  const trackTemplateUsage = async (templateId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;
      
      // Find template in DB
      const dbTemplate = dbTemplates.find(t => 
        t.id === templateId || 
        t.template_data.id === templateId
      );
      
      if (dbTemplate && dbTemplate.id) {
        // Update the usage timestamp
        await supabase
          .from('user_templates')
          .update({
            updated_at: new Date().toISOString()
          })
          .eq('id', dbTemplate.id);
        
        console.log('Updated template usage timestamp');
      }
    } catch (error) {
      console.error('Error tracking template usage:', error);
    }
  };

  // Template management functions
  const saveTemplate = async (templateData: Omit<ThemeTemplate, 'id' | 'isDefault'>, existingId?: string) => {
    // Apply the font weight CSS variables immediately
    if (templateData.currentLineFontWeight !== undefined) {
      // For a value of 0, explicitly set "0" rather than an empty string
      document.documentElement.style.setProperty(
        '--current-line-font-weight', 
        templateData.currentLineFontWeight === 0 ? "0" : templateData.currentLineFontWeight.toString()
      );
      const fontWeightValue = 400 + (300 * templateData.currentLineFontWeight);
      document.documentElement.style.setProperty('--lyrics-line-font-weight', fontWeightValue.toString());
      
      // Log the actual value being saved
      console.log('Saving template with explicit font weight:', 
                  templateData.currentLineFontWeight,
                  'CSS value:', document.documentElement.style.getPropertyValue('--current-line-font-weight'));
    }
    
    // Create or update the template in local state
    let localTemplateId: string;
    
    // If we have an existing ID, we're updating rather than creating
    if (existingId) {
      const template = templates.find(t => t.id === existingId);
      if (template) {
        const isDefault = template.isDefault;
        setTemplates(prev => 
          prev.map(t => t.id === existingId ? {
            ...templateData,
            id: existingId,
            isDefault
          } : t)
        );
        localTemplateId = existingId;
      } else {
        // If template not found with that ID, create a new one
        const newTemplate: ThemeTemplate = {
          ...templateData,
          id: `template_${Date.now()}`,
          isDefault: false
        };
        
        setTemplates(prev => [...prev, newTemplate]);
        localTemplateId = newTemplate.id;
      }
    } else {
    // Otherwise create a new template
    const newTemplate: ThemeTemplate = {
      ...templateData,
      id: `template_${Date.now()}`,
      isDefault: false
    };
    
    setTemplates(prev => [...prev, newTemplate]);
      localTemplateId = newTemplate.id;
    }
    
    // Save to Supabase if user is logged in
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Find existing remote template by name or ID
        const existingDbTemplate = dbTemplates.find(t => 
          (existingId && t.template_data.id === existingId) || 
          t.name === templateData.name
        );
        
        if (existingDbTemplate) {
          // Update existing template
          const { data, error } = await supabase
            .from('user_templates')
            .update({
              name: templateData.name,
              template_data: {
                ...templateData,
                id: existingId || localTemplateId,
                isDefault: false
              },
              updated_at: new Date().toISOString()
            })
            .eq('id', existingDbTemplate.id)
            .select();
            
          if (error) {
            console.error('Error updating template in Supabase:', error);
          } else {
            console.log('Template updated in Supabase:', data);
            // Refresh templates from DB
            fetchUserTemplates();
          }
        } else {
          // Create new template
          const { data, error } = await supabase
            .from('user_templates')
            .insert({
              user_id: user.id,
              name: templateData.name,
              description: 'User created template',
              priority: 0, // Default priority
              template_data: {
                ...templateData,
                id: localTemplateId,
                isDefault: false
              }
            })
            .select();
            
          if (error) {
            console.error('Error saving template to Supabase:', error);
          } else {
            console.log('Template saved to Supabase:', data);
            // Refresh templates from DB
            fetchUserTemplates();
          }
        }
      }
    } catch (error) {
      console.error('Error saving template to Supabase:', error);
    }
    
    return localTemplateId;
  };
  
  const deleteTemplate = async (id: string) => {
    // Don't allow deleting default templates
    const template = templates.find(t => t.id === id);
    if (!template || template.isDefault) return;
    
    // Remove from local state
    setTemplates(prev => prev.filter(t => t.id !== id));
    
    // Delete from Supabase if user is logged in
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Find the DB template with matching template_data.id
        const dbTemplate = dbTemplates.find(t => t.template_data.id === id);
        
        if (dbTemplate) {
          const { error } = await supabase
            .from('user_templates')
            .delete()
            .eq('id', dbTemplate.id);
            
          if (error) {
            console.error('Error deleting template from Supabase:', error);
          } else {
            console.log('Template deleted from Supabase');
            // Refresh templates from DB
            fetchUserTemplates();
          }
        }
      }
    } catch (error) {
      console.error('Error deleting template from Supabase:', error);
    }
  };
  
  const setDefaultTemplate = async (id: string) => {
    // Find the template in local state
    const template = templates.find(t => t.id === id);
    if (!template) return;
    
    // Check if we're setting or clearing the default
    const isSettingDefault = !templates.find(t => t.isDefault && t.id === id);
    
    // Update local state
    setTemplates(prev => 
      prev.map(t => ({
        ...t,
        isDefault: t.id === id && isSettingDefault
      }))
    );
    
    // Update in Supabase if user is logged in
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Find the DB template with matching template_data.id
        const dbTemplate = dbTemplates.find(t => t.template_data.id === id);
        
        // First, get all user templates so we can update them
        const { data: userTemplates } = await supabase
          .from('user_templates')
          .select('*')
          .eq('user_id', user.id);
          
        if (userTemplates) {
          // Set all templates to non-default (priority = 0)
          for (const tmpl of userTemplates) {
            // Update template to set isDefault to false and priority to 0
            await supabase
              .from('user_templates')
              .update({
                priority: 0,
                template_data: {
                  ...tmpl.template_data,
                  isDefault: false
                }
              })
              .eq('id', tmpl.id);
          }
        }
          
        // If we're clearing the default, we're done
        if (!isSettingDefault) {
          console.log('Default template cleared in Supabase');
          // Refresh templates from DB
          fetchUserTemplates();
          return;
        }
        
        if (dbTemplate) {
          // Set this template to highest priority and update isDefault in the template_data
          const updatedTemplateData = {
            ...dbTemplate.template_data,
            isDefault: true
          };
          
          const { error } = await supabase
            .from('user_templates')
            .update({ 
              priority: 100,
              template_data: updatedTemplateData
            })
            .eq('id', dbTemplate.id);
            
          if (error) {
            console.error('Error setting default template in Supabase:', error);
          } else {
            console.log('Default template set in Supabase');
            // Refresh templates from DB
            fetchUserTemplates();
          }
        }
      }
    } catch (error) {
      console.error('Error setting default template in Supabase:', error);
    }
  };

  // Simplified state helper
  const isTemplateDark = colorMode === 'dark';

  // Watch for track changes to ensure template settings persist
  useEffect(() => {
    if (trackIdJustChanged && currentTrackId) {
      console.log('Track changed, ensuring template settings are applied');
      
      // Get the currently selected template ID
      const savedTemplateId = localStorage.getItem('current_template');
      
      if (savedTemplateId) {
        // Try to find the template in local templates first
        let template = templates.find(t => t.id === savedTemplateId);
        
        // If not found in local templates, check in DB templates
        if (!template && dbTemplates.length > 0) {
          const dbTemplate = dbTemplates.find(t => 
            t.id === savedTemplateId || 
            t.template_data.id === savedTemplateId
          );
          if (dbTemplate) {
            template = dbTemplate.template_data;
          }
        }
        
        // If template was found, apply any necessary CSS variables again
        if (template) {
          console.log('Reapplying template CSS variables for new track:', template.name);
          
          // Ensure CSS variables are properly set
          document.documentElement.style.setProperty('--current-line-font-weight', 
            template.currentLineFontWeight !== undefined ? template.currentLineFontWeight.toString() : '0.6');
          
          const fontWeightValue = 400 + (300 * (template.currentLineFontWeight || 0.6));
          document.documentElement.style.setProperty('--lyrics-line-font-weight', fontWeightValue.toString());
          
          document.documentElement.style.setProperty('--frosted-effect-enabled', 
            template.frostedEffect ? '1' : '0');
          
          document.documentElement.style.setProperty('--frosted-effect-opacity', 
            template.frostedOpacity?.toString() || '0.3');
            
          document.documentElement.style.setProperty('--lyrics-line-letter-spacing', 
            template.letterSpacing || '-0.04em');
            
          document.documentElement.style.setProperty('--current-line-scale', 
            template.currentLineScale?.toString() || '1.1');
            
          document.documentElement.style.setProperty('--current-line-font-factor', 
            template.currentLineFontFactor?.toString() || '0.7');
        }
      }
    }
  }, [currentTrackId, trackIdJustChanged, templates, dbTemplates]);

  // Add a function to save all layout settings at once
  const saveLayoutToLocalStorage = () => {
    const layoutSettings: LayoutSettings = {
      fontSize,
      lineSpacing,
      letterSpacing,
      currentLineScale,
      currentLineFontFactor,
      currentLineFontWeight,
      frostedEffect,
      frostedOpacity,
      multiplier
    };
    
    try {
      localStorage.setItem('lyrics_layout', JSON.stringify(layoutSettings));
      console.log('Layout settings saved to localStorage', layoutSettings);
    } catch (error) {
      console.error('Error saving layout settings to localStorage:', error);
    }
  };

  // Update the existing useEffects to call this function
  useEffect(() => {
    // Previous code
    localStorage.setItem('font_size', JSON.stringify(fontSize));
    
    // Add the combined layout save
    saveLayoutToLocalStorage();
  }, [fontSize]);

  useEffect(() => {
    // Previous code
    localStorage.setItem('line_spacing', JSON.stringify(lineSpacing));
    
    // Add the combined layout save
    saveLayoutToLocalStorage();
  }, [lineSpacing]);

  useEffect(() => {
    // Previous code
    localStorage.setItem('letter_spacing', JSON.stringify(letterSpacing));
    
    // Set the letter spacing as a CSS variable
    document.documentElement.style.setProperty('--lyrics-line-letter-spacing', letterSpacing);
    
    // Add the combined layout save
    saveLayoutToLocalStorage();
  }, [letterSpacing]);

  useEffect(() => {
    // Previous code
    localStorage.setItem('current_line_scale', JSON.stringify(currentLineScale));
    
    // Set the current line scale as a CSS variable
    document.documentElement.style.setProperty('--current-line-scale', currentLineScale.toString());
    
    // Add the combined layout save
    saveLayoutToLocalStorage();
  }, [currentLineScale]);

  useEffect(() => {
    // Previous code
    localStorage.setItem('current_line_font_factor', JSON.stringify(currentLineFontFactor));
    
    // Set the current line font factor as a CSS variable
    document.documentElement.style.setProperty('--current-line-font-factor', currentLineFontFactor.toString());
    
    // Add the combined layout save
    saveLayoutToLocalStorage();
  }, [currentLineFontFactor]);

  useEffect(() => {
    try {
      // Previous code
      localStorage.setItem('current_line_font_weight', JSON.stringify(currentLineFontWeight));
      
      // Set the current line font weight as a CSS variable
      document.documentElement.style.setProperty(
        '--current-line-font-weight', 
        currentLineFontWeight === 0 ? "0" : currentLineFontWeight.toString()
      );
      
      // Also set a directly usable font-weight value for CSS fallback
      const fontWeightValue = 400 + (300 * currentLineFontWeight);
      document.documentElement.style.setProperty('--lyrics-line-font-weight', fontWeightValue.toString());
      
      // Add the combined layout save
      saveLayoutToLocalStorage();
      
      // For debugging
      console.log('Updated currentLineFontWeight:', currentLineFontWeight, 
                  'CSS variable:', document.documentElement.style.getPropertyValue('--current-line-font-weight'),
                  'Font weight value:', fontWeightValue);
    } catch (error) {
      console.error('Error saving currentLineFontWeight:', error);
    }
  }, [currentLineFontWeight]);

  useEffect(() => {
    // Previous code
    localStorage.setItem('frosted_effect', JSON.stringify(frostedEffect));
    
    // Set frosted effect as CSS variable
    document.documentElement.style.setProperty('--frosted-effect-enabled', frostedEffect ? '1' : '0');
    
    // Add the combined layout save
    saveLayoutToLocalStorage();
  }, [frostedEffect]);

  useEffect(() => {
    // Previous code
    localStorage.setItem('frosted_opacity', JSON.stringify(frostedOpacity));
    
    // Set frosted opacity as CSS variable
    document.documentElement.style.setProperty('--frosted-effect-opacity', frostedOpacity.toString());
    
    // Add the combined layout save
    saveLayoutToLocalStorage();
  }, [frostedOpacity]);

  useEffect(() => {
    // Previous code
    localStorage.setItem('multiplier', JSON.stringify(multiplier));
    
    // Add the combined layout save
    saveLayoutToLocalStorage();
  }, [multiplier]);

  // Add a function to load all layout settings at once - add after the loadFromStorage function
  const loadLayoutFromLocalStorage = () => {
    try {
      const savedLayout = localStorage.getItem('lyrics_layout');
      if (savedLayout) {
        const layoutSettings: LayoutSettings = JSON.parse(savedLayout);
        console.log('Loading layout settings from localStorage', layoutSettings);
        
        // Apply the loaded settings
        if (layoutSettings.fontSize !== undefined) setFontSize(layoutSettings.fontSize);
        if (layoutSettings.lineSpacing !== undefined) setLineSpacing(layoutSettings.lineSpacing);
        if (layoutSettings.letterSpacing !== undefined) setLetterSpacing(layoutSettings.letterSpacing);
        if (layoutSettings.currentLineScale !== undefined) setCurrentLineScale(layoutSettings.currentLineScale);
        if (layoutSettings.currentLineFontFactor !== undefined) setCurrentLineFontFactor(layoutSettings.currentLineFontFactor);
        if (layoutSettings.currentLineFontWeight !== undefined) setCurrentLineFontWeight(layoutSettings.currentLineFontWeight);
        if (layoutSettings.frostedEffect !== undefined) setFrostedEffect(layoutSettings.frostedEffect);
        if (layoutSettings.frostedOpacity !== undefined) setFrostedOpacity(layoutSettings.frostedOpacity);
        if (layoutSettings.multiplier !== undefined) setMultiplier(layoutSettings.multiplier);
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error loading layout settings from localStorage:', error);
      return false;
    }
  };

  // Add an effect to load layout settings on mount
  useEffect(() => {
    // Load layout settings
    loadLayoutFromLocalStorage();
  }, []);

  // Update the track change effect to also ensure layout is preserved
  useEffect(() => {
    if (trackIdJustChanged && currentTrackId) {
      console.log('Track changed, ensuring template and layout settings are applied');
      
      // First re-apply any saved layout settings
      loadLayoutFromLocalStorage();
      
      // Then get the currently selected template ID
      const savedTemplateId = localStorage.getItem('current_template');
      
      if (savedTemplateId) {
        // Try to find the template in local templates first
        let template = templates.find(t => t.id === savedTemplateId);
        
        // If not found in local templates, check in DB templates
        if (!template && dbTemplates.length > 0) {
          const dbTemplate = dbTemplates.find(t => 
            t.id === savedTemplateId || 
            t.template_data.id === savedTemplateId
          );
          if (dbTemplate) {
            template = dbTemplate.template_data;
          }
        }
        
        // If template was found, apply any necessary CSS variables again
        if (template) {
          console.log('Reapplying template CSS variables for new track:', template.name);
          
          // Ensure CSS variables are properly set
          document.documentElement.style.setProperty('--current-line-font-weight', 
            template.currentLineFontWeight !== undefined ? template.currentLineFontWeight.toString() : '0.6');
          
          const fontWeightValue = 400 + (300 * (template.currentLineFontWeight || 0.6));
          document.documentElement.style.setProperty('--lyrics-line-font-weight', fontWeightValue.toString());
          
          document.documentElement.style.setProperty('--frosted-effect-enabled', 
            template.frostedEffect ? '1' : '0');
          
          document.documentElement.style.setProperty('--frosted-effect-opacity', 
            template.frostedOpacity?.toString() || '0.3');
            
          document.documentElement.style.setProperty('--lyrics-line-letter-spacing', 
            template.letterSpacing || '-0.04em');
            
          document.documentElement.style.setProperty('--current-line-scale', 
            template.currentLineScale?.toString() || '1.1');
            
          document.documentElement.style.setProperty('--current-line-font-factor', 
            template.currentLineFontFactor?.toString() || '0.7');
        }
      }
    }
  }, [currentTrackId, trackIdJustChanged, templates, dbTemplates]);

  // Provide the context value
  const contextValue: LyricsThemeContextType = {
    currentTheme,
    setCurrentTheme,
    colorMode,
    setColorMode,
    isDarkMode,
    isLightMode,
    isTemplateDark,
    dominantColor,
    setDominantColor,
    isDominantColorLoading,
    setIsDominantColorLoading,
    backgroundGradient,
    setBackgroundGradient,
    fontSize,
    setFontSize,
    lineSpacing,
    setLineSpacing,
    multiplier,
    setMultiplier,
    backgroundType,
    setBackgroundType,
    backgroundColor,
    setBackgroundColor,
    activeTextColor,
    setActiveTextColor,
    inactiveTextColor,
    setInactiveTextColor,
    currentLineScale,
    setCurrentLineScale,
    currentLineFontFactor,
    setCurrentLineFontFactor,
    transitionType,
    setTransitionType,
    frostedEffect,
    setFrostedEffect,
    frostedOpacity,
    setFrostedOpacity,
    frostedColorDark,
    setFrostedColorDark,
    frostedColorLight,
    setFrostedColorLight,
    templates,
    dbTemplates,
    isLoadingDbTemplates,
    dbTemplateError,
    fetchUserTemplates,
    currentTemplate,
    saveTemplate,
    deleteTemplate,
    applyTemplate,
    setDefaultTemplate,
    currentLineFontWeight,
    setCurrentLineFontWeight,
    letterSpacing,
    setLetterSpacing
  };

  return (
    <LyricsThemeContext.Provider value={contextValue}>
      {children}
    </LyricsThemeContext.Provider>
  );
};

// Custom hook to use the lyrics theme context
export const useLyricsTheme = () => useContext(LyricsThemeContext); 